import { create } from 'zustand';

import { fetchAppConfig } from '@/services/config/app-config.service';

interface JsonMap {
  [key: string]: unknown;
}

type AppConfigStatus = 'idle' | 'loading' | 'success' | 'failed';

interface AppConfigState {
  status: AppConfigStatus;
  loadedTenantId: string | null;
  error: string | null;
  config: JsonMap;
  planConfig: JsonMap;
  mergedConfig: JsonMap;
  loadConfig: (tenantId: string) => Promise<void>;
  reset: () => void;
}

export const useAppConfigStore = create<AppConfigState>((set, get) => ({
  status: 'idle',
  loadedTenantId: null,
  error: null,
  config: {},
  planConfig: {},
  mergedConfig: {},
  loadConfig: async (tenantId: string): Promise<void> => {
    const normalizedTenantId = tenantId.toLowerCase();
    const { status, loadedTenantId } = get();
    if (loadedTenantId === normalizedTenantId && (status === 'loading' || status === 'success')) {
      return;
    }
    set({ status: 'loading', error: null, loadedTenantId: normalizedTenantId });
    try {
      const payload = await fetchAppConfig(normalizedTenantId);
      if (get().loadedTenantId !== normalizedTenantId) {
        return;
      }
      set({
        status: 'success',
        error: null,
        config: payload.config,
        planConfig: payload.planConfig,
        mergedConfig: payload.mergedConfig,
      });
    } catch (error: unknown) {
      if (get().loadedTenantId !== normalizedTenantId) {
        return;
      }
      set({
        status: 'failed',
        error: error instanceof Error ? error.message : String(error),
      });
    }
  },
  reset: () => {
    set({
      status: 'idle',
      loadedTenantId: null,
      error: null,
      config: {},
      planConfig: {},
      mergedConfig: {},
    });
  },
}));
